import { useState, useEffect, useRef } from 'react';
import { message } from 'antd';
import { APIClient } from "../../helpers/api_helper";

const api = new APIClient();

const INITIAL_BUDGET_PLAN_DATA = {
  production_fee_percent_full: 0,
  production_fee_percent_discounted: 0,
  not_forseen_percent_full: 5,
  not_forseen_percent_discounted: 0,
  discount_percent: 0,
  tax_rate: '22.0'
};

// The percent inputs emit null when cleared, and TaxRateSelect emits null the
// moment "OTHR" is clicked. The backend treats a null as 0, so send it 0 rather
// than the null -- otherwise save_budget_draft persists the null into
// Project.budget_plan_data and every later load recalculates from bad data.
// Also drops the display-only form fields (production_fee_amount, ...) that
// antd collects alongside the real settings.
const BUDGET_PLAN_FIELDS = Object.keys(INITIAL_BUDGET_PLAN_DATA);

const normalizeBudgetPlanData = (data) =>
  Object.fromEntries(
    BUDGET_PLAN_FIELDS.map(key => {
      const val = data?.[key];
      return [key, val === null || val === undefined || val === '' ? 0 : val];
    })
  );

// "vrstica 2" / "vrstici 2, 3" / "vrstice 2, 3, 4" / "vrstic 2, 3, 4, 5, 6"
const rowsWord = (n) => (n === 1 ? 'vrstica' : n === 2 ? 'vrstici' : n <= 4 ? 'vrstice' : 'vrstic');

// The rows are highlighted in the table too; this names them so the user knows
// which category to scroll to when the plan runs over several screens.
const incompleteRowsMessage = (problems = []) => {
  const detail = problems
    .map(p => `${p.category} - ${rowsWord(p.rows.length)} ${p.rows.join(', ')}`)
    .join('; ');
  return detail
    ? `Prosimo, izpolnite vsa obvezna polja (Naziv in Cena): ${detail}`
    : 'Prosimo, izpolnite vsa obvezna polja (Naziv in Cena)';
};

const useBudgetPlanData = ({
  projData,
  loadProject,
  selectedVersion,
  setSelectedVersion,
  setHasDraft
}) => {
  const [totalsData, setTotalsData] = useState({});
  const [expensesByCategoryData, setExpensesByCategoryData] = useState([]);
  const [budgetPlanSettings, setBudgetPlanSettings] = useState(INITIAL_BUDGET_PLAN_DATA);
  const [incomePlanRows, setIncomePlanRows] = useState([]);
  const [editingSection, setEditingSection] = useState(null);
  const [hasIntents, setHasIntents] = useState(false);
  const [categoriesWithoutDate, setCategoriesWithoutDate] = useState([]);
  const [validateRowsFn, setValidateRowsFn] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [lastLoadedAt, setLastLoadedAt] = useState(null);
  const [isSavingVersion, setIsSavingVersion] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isLoadingVersion, setIsLoadingVersion] = useState(false);
  const lastLoadedVersionRef = useRef(null);

  const isReadOnly = !isEditing;

  // Change tracking wrappers
  const handleExpensesChange = (data) => {
    setExpensesByCategoryData(data);
    setHasChanges(true);
  };

  const handleBudgetPlanSettingsChange = (data) => {
    setBudgetPlanSettings(data);
    setHasChanges(true);
  };

  const handleIncomePlanChange = (dataOrFn) => {
    setIncomePlanRows(dataOrFn);
    setHasChanges(true);
  };

  const getPayload = () => {
    const expensesWithInx = expensesByCategoryData.map((section, sectionInx) => ({
      ...section,
      inx: sectionInx,
      rows: section.rows.map((row, rowInx) => ({
        ...row,
        inx: rowInx
      }))
    }));
    const incomePlanWithInx = incomePlanRows.map((row, inx) => ({
      ...row,
      inx
    }));
    return {
      'expenses_by_category': expensesWithInx,
      'budget_plan_data': normalizeBudgetPlanData(budgetPlanSettings),
      'income_plan_rows': incomePlanWithInx
    };
  };

  const calcAndSetTotals = () => {
    if (!projData.id) return;
    const payload = getPayload();
    api.post(`/api/projects/${projData.id}/get_budget_totals/`, payload, { successMsg: null })
      .then(resp => setTotalsData(resp))
      .catch(err => console.error('API error:', err));
  };

  const loadBudgetExpensesPlanData = (version = null, setVersionFromResponse = true) => {
    const params = version && version !== 'draft' ? { version } : {};
    setIsLoadingVersion(true);
    api.get(`/api/projects/${projData.id}/get_budget_data/`, params)
      .then(resp => {
        setExpensesByCategoryData(resp.expenses_by_category);
        setIncomePlanRows(resp.income_plan_rows || []);
        setHasDraft(resp.has_draft);
        setHasChanges(false);
        setIsEditing(false);
        setLastLoadedAt(Date.now() / 1000);

        const loadedVersion = resp.current_version === null ? 'draft' : resp.current_version;
        lastLoadedVersionRef.current = loadedVersion;

        if (setVersionFromResponse) {
          setSelectedVersion(loadedVersion);
        }

        const bud = Object.keys(resp.budget_plan_data || {}).length === 0
          ? INITIAL_BUDGET_PLAN_DATA
          : normalizeBudgetPlanData(resp.budget_plan_data);
        setBudgetPlanSettings(bud);
      })
      .catch(err => console.error('API error:', err))
      .finally(() => setIsLoadingVersion(false));
  };

  const onSubmit = () => {
    const rowCheck = validateRowsFn ? validateRowsFn() : { valid: true };
    if (!rowCheck.valid) {
      message.error(incompleteRowsMessage(rowCheck.problems), 6);
      return;
    }
    setIsSavingDraft(true);
    api.create(`/api/projects/${projData.id}/save_budget_draft/`, getPayload(), { successMsg: `Osnutek uspešno shranjen` })
      .then(() => {
        setEditingSection(null);
        setSelectedVersion('draft');
        setIsEditing(false);
        loadBudgetExpensesPlanData(null, false);
      })
      .catch(err => console.error('API error:', err))
      .finally(() => setIsSavingDraft(false));
  };

  const handleSaveNewBudgetVersion = () => {
    if (categoriesWithoutDate.length > 0) {
      message.error(`Manjka datum za kategorije: ${categoriesWithoutDate.join(', ')}`);
      return;
    }
    setIsSavingVersion(true);
    api.post(`/api/projects/${projData.id}/save_new_budget_version/`, {}, { successMsg: `Nova različica proračuna uspešno shranjena!` })
      .then(resp => {
        loadProject();
        loadBudgetExpensesPlanData(null, true);
        console.log('Created intents from budget:', resp);
      })
      .catch(err => console.error('API error:', err))
      .finally(() => setIsSavingVersion(false));
  };

  const onStartEditing = () => {
    // Switch to draft mode while keeping current data
    lastLoadedVersionRef.current = 'draft';
    setSelectedVersion('draft');
    setHasDraft(true);
    setHasChanges(true);
    setIsEditing(true);
  };

  // Wrap setEditingSection so that entering any section also triggers global edit mode
  const handleSetEditingSection = (val) => {
    setEditingSection(val);
    if (val !== null) {
      onStartEditing();
    }
  };

  // Load data when project changes
  useEffect(() => {
    if (projData.id) {
      loadBudgetExpensesPlanData(null);
    }
  }, [projData.id, projData.version]);

  // Reload when version selection changes
  useEffect(() => {
    if (projData.id && selectedVersion !== null && selectedVersion !== lastLoadedVersionRef.current) {
      loadBudgetExpensesPlanData(selectedVersion === 'draft' ? null : selectedVersion, false);
    }
  }, [selectedVersion]);

  // Recalculate totals when data changes
  useEffect(() => {
    calcAndSetTotals();
  }, [expensesByCategoryData, budgetPlanSettings]);

  return {
    // State
    totalsData,
    expensesByCategoryData,
    budgetPlanSettings,
    incomePlanRows,
    editingSection,
    hasIntents,
    categoriesWithoutDate,
    hasChanges,
    isEditing,
    isReadOnly,
    lastLoadedAt,
    isSavingVersion,
    isSavingDraft,
    isLoadingVersion,

    // Setters with change tracking
    setExpensesData: handleExpensesChange,
    setBudgetPlanSettings: handleBudgetPlanSettingsChange,
    setIncomePlanRows: handleIncomePlanChange,
    setEditingSection: handleSetEditingSection,
    setHasIntents,
    setCategoriesWithoutDate,
    setValidateRowsFn,

    // Actions
    onSubmit,
    handleSaveNewBudgetVersion,
    loadBudgetExpensesPlanData,
    onStartEditing,

    // Computed
    nextVersion: (projData.version || 0) + 1,
  };
};

export default useBudgetPlanData;
